import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Zap, Star, CheckCircle, MapPin, Leaf, Mail } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { useEcologi } from "@/components/EcologiBadge";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const viewportOnce = { once: true, amount: 0.05 };

const careersHeroInitial = { opacity: 0, scale: 0.95 };
const careersHeroAnimate = { opacity: 1, scale: 1 };
const careersHeroTransition = { duration: 0.5, delay: 0.2 };

const TEAM_PHOTO = "https://cdn.candopayments.com/team/team_colour_web_solid.jpg";

const WHY_JOIN = [
  { icon: Heart, title: "Family Culture", desc: "We're a close-knit team that looks after each other. No corporate egos, no politics. If you've had a tough morning, go for a walk. We've got your back." },
  { icon: Users, title: "Real Impact", desc: "You'll work directly with UK business owners and see the difference your work makes every single day. No layers. No red tape." },
  { icon: Zap, title: "Growth Opportunity", desc: "We're growing fast. Join now and grow with us — there's room to shape your own role and build something you're proud of." },
  { icon: Star, title: "Honest Environment", desc: "We say what we mean and do what we say. Transparent with our clients and transparent with each other." },
];

const BENEFITS = [
  "Company pension scheme from day one",
  "Cycle to Work scheme (save up to 42%)",
  "IT & tech hardware through salary sacrifice",
  "Employee discounts at major retailers (BHN Extras)",
  "Flexible working — we trust our team",
  "Mental health and wellbeing support",
  "Regular team days out (yes, bowling counts)",
  "Growth, training, and development support",
];

export default function CareersPage() {
  const eco = useEcologi();

  return (
    <div>
      <SEOHead title="Join Our Team: Career & Job Opportunities" description="Build your future with CanDo Payments! Check out our latest career openings and job opportunities. Join a dynamic team committed to revolutionising payments." path="/careers" />

      {/* Hero — 2 column with team photo */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Careers at CanDo</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">Work with people who <span className="gradient-text">genuinely care</span></motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-4">We&apos;re not a faceless corporation. We&apos;re a family business that&apos;s building something special — and we need brilliant people to help us get there.</motion.p>
              <motion.div variants={fadeUp} className="flex items-center gap-2 text-sm text-brand-black mb-8">
                <MapPin size={16} className="text-brand-aqua" /> Ellesmere Port, Cheshire (with flexible working)
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <a href="#open-roles" className="cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-all btn-shine">View open roles <ArrowRight size={16} /></a>
                <a href="#speculative" className="bg-white text-brand-black hover:bg-gray-50 border border-brand-light-grey font-semibold rounded-full px-8 py-3.5 text-sm transition-colors">Send a speculative application</a>
              </motion.div>
            </motion.div>
            <motion.div initial={careersHeroInitial} animate={careersHeroAnimate} transition={careersHeroTransition} className="hidden lg:block">
              <img src={TEAM_PHOTO} alt="The CanDo Payments team" className="rounded-2xl shadow-xl w-full" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why join */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-8 md:mb-12">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Why CanDo?</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">This isn&apos;t just a job. It&apos;s a journey.</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_JOIN.map((item) => (
              <div key={item.title} className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6 hover:shadow-md transition-shadow">
                <item.icon size={24} className="text-brand-aqua mb-4" />
                <h3 className="font-heading font-bold text-brand-black mb-2">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote + culture */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-brand-light-grey rounded-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 sm:p-10 space-y-5">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">What our team says</p>
                <p className="text-xl font-heading text-brand-black leading-relaxed italic">&ldquo;I&apos;m feeling really good about work and you being the boss is really having a good impact on me.&rdquo;</p>
                <p className="text-sm text-brand-aqua font-semibold">— CanDo Team Member</p>
                <div className="border-t border-brand-light-grey pt-5 mt-5">
                  <p className="text-sm text-brand-grey leading-relaxed">Every person at CanDo chose to be here. We&apos;re hungry, we&apos;re driven, and we support each other through the hard days and celebrate the wins together. That&apos;s not a strapline — it&apos;s how it actually works.</p>
                </div>
              </div>
              <div className="bg-brand-black p-8 sm:p-10 text-white flex flex-col justify-center">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua mb-4">More than just a salary</p>
                <div className="space-y-2.5">
                  {BENEFITS.map((b) => (
                    <div key={b} className="flex items-start gap-2 text-sm text-white/80">
                      <CheckCircle size={14} className="text-brand-aqua flex-shrink-0 mt-0.5" />{b}
                    </div>
                  ))}
                </div>
                <a href="https://www.bhnextras.co.uk/" target="_blank" rel="noopener noreferrer" className="text-xs text-brand-aqua font-semibold mt-4 hover:underline">
                  Benefits powered by BHN Extras →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Open roles */}
      <section id="open-roles" className="py-16 md:py-20 bg-white scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-10">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Open Positions</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Current opportunities</motion.h2>
          </motion.div>

          {/* Placeholder for when roles are available */}
          <div className="bg-gray-50 border border-brand-light-grey rounded-2xl p-8 sm:p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-brand-aqua-light flex items-center justify-center mx-auto mb-4">
              <Users size={28} className="text-brand-aqua" />
            </div>
            <h3 className="font-heading text-xl font-bold text-brand-black mb-3">No specific vacancies right now</h3>
            <p className="text-brand-grey leading-relaxed max-w-lg mx-auto mb-6">We&apos;re always growing and always interested in hearing from exceptional people. If you think you&apos;d be a great fit for CanDo, don&apos;t wait for a vacancy — tell us why we need you.</p>
            <a href="#speculative" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-aqua hover:text-brand-aqua-dark transition-colors">
              Send a speculative application <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Values strip */}
      <section className="py-12 bg-brand-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {[
              { value: "Family", label: "Owned & Run" },
              { value: `${eco.trees > 0 ? eco.trees.toLocaleString() : "..."}`, label: "Trees Planted" },
              { value: "B Corp", label: "Certified" },
              { value: "<4 min", label: "Support Response" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl sm:text-3xl font-heading font-bold text-brand-aqua">{s.value}</p>
                <p className="text-xs text-brand-grey uppercase tracking-wider mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Speculative application */}
      <section id="speculative" className="py-16 md:py-20 bg-gray-50 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Get In Touch</p>
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">Think you&apos;d be a great fit?</h2>
            <p className="text-brand-grey leading-relaxed mb-6">We don&apos;t always have roles advertised — but we&apos;re always open to meeting talented people. Tell us who you are, what you&apos;re passionate about, and why CanDo Payments feels like the right place for you.</p>
            <div className="space-y-3">
              {[
                "We read every application personally",
                "No AI screening — Gary or Sam will respond",
                "We care about attitude as much as experience",
                "Remote and flexible options available",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                  <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3">
              <Leaf size={16} className="text-green-500" />
              <p className="text-xs text-brand-grey">Every application plants a tree in our Ecologi forest.</p>
            </div>
          </div>
          <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
            {/* Applications go to the inbox, not the CRM — they aren't sales leads. */}
            <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Send us your CV</h3>
            <p className="text-sm text-brand-grey mb-6">Email your CV with a few lines about yourself and what excites you about CanDo. We read every application and reply personally.</p>
            <a
              href="mailto:hello@candopayments.com?subject=Careers%20at%20CanDo"
              className="w-full btn-form-ready is-ready text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2"
              data-testid="careers-email-cv"
            >
              <Mail size={16} />
              <span>Email your CV</span>
            </a>
            <p className="text-xs text-brand-grey mt-3 text-center">hello@candopayments.com</p>
          </div>
        </div>
      </section>
    </div>
  );
}
